import { get, post, put, del } from '../apiClient';
import type {
  AppSettings,
  UpdateSettingsRequest,
  ModuleConfigurationAdmin,
  CreateModuleConfigRequest,
  UpdateModuleConfigRequest,
  ApplicationUser,
  CreateUserRequest,
  UpdateUserRoleRequest,
  AzureAdUser,
  AuditLogEntry,
  AuditLogFilters,
  PaginatedList,
  TeamsTestNotificationResult,
} from '@/types';

// Raw shape returned by GET /settings
interface GlobalSettingDto {
  id: string;
  key: string;
  value: string;
  valueType: string;
  description: string | null;
  isEditable: boolean;
}

function transformSettings(settings: GlobalSettingDto[]): AppSettings {
  const map = new Map(settings.map((s) => [s.key, s.value]));

  const getStr = (key: string) => map.get(key) ?? '';
  const getInt = (key: string) => parseInt(getStr(key), 10) || 0;
  const getBool = (key: string) => getStr(key).toLowerCase() === 'true';

  return {
    id: settings[0]?.id ?? '',
    defaultTimeoutSeconds: getInt('DefaultTimeoutSeconds'),
    maxRetryAttempts: getInt('MaxRetryAttempts'),
    retryDelaySeconds: getInt('RetryDelaySeconds'),
    enableEmailNotifications: getBool('EnableEmailNotifications'),
    enableTeamsNotifications: getBool('EnableTeamsNotifications'),
    teamsWebhookUrl: getStr('TeamsWebhookUrl') || null,
    notificationRecipients: getStr('NotificationRecipients')
      ? getStr('NotificationRecipients').split(',').map((e) => e.trim()).filter(Boolean)
      : [],
    bridgeBaseUrl: getStr('BridgeBaseUrl'),
    bridgeApiKeySecretName: getStr('BridgeApiKeySecretName'),
    amplifyBaseUrl: getStr('AmplifyBaseUrl'),
    amplifyApiKeySecretName: getStr('AmplifyApiKeySecretName'),
    updatedAt: new Date().toISOString(),
    updatedByUserName: '',
  };
}

// Map UpdateSettingsRequest fields to GlobalSetting keys + serialized values
function toSettingEntries(data: UpdateSettingsRequest): Array<{ key: string; value: string }> {
  const entries: Array<{ key: string; value: string }> = [];

  if (data.defaultTimeoutSeconds !== undefined)
    entries.push({ key: 'DefaultTimeoutSeconds', value: String(data.defaultTimeoutSeconds) });
  if (data.maxRetryAttempts !== undefined)
    entries.push({ key: 'MaxRetryAttempts', value: String(data.maxRetryAttempts) });
  if (data.retryDelaySeconds !== undefined)
    entries.push({ key: 'RetryDelaySeconds', value: String(data.retryDelaySeconds) });
  if (data.enableEmailNotifications !== undefined)
    entries.push({ key: 'EnableEmailNotifications', value: String(data.enableEmailNotifications) });
  if (data.enableTeamsNotifications !== undefined)
    entries.push({ key: 'EnableTeamsNotifications', value: String(data.enableTeamsNotifications) });
  if (data.teamsWebhookUrl !== undefined)
    entries.push({ key: 'TeamsWebhookUrl', value: data.teamsWebhookUrl ?? '' });
  if (data.notificationRecipients !== undefined)
    entries.push({ key: 'NotificationRecipients', value: data.notificationRecipients.join(', ') });
  if (data.bridgeBaseUrl !== undefined)
    entries.push({ key: 'BridgeBaseUrl', value: data.bridgeBaseUrl });
  if (data.bridgeApiKeySecretName !== undefined)
    entries.push({ key: 'BridgeApiKeySecretName', value: data.bridgeApiKeySecretName });
  if (data.amplifyBaseUrl !== undefined)
    entries.push({ key: 'AmplifyBaseUrl', value: data.amplifyBaseUrl });
  if (data.amplifyApiKeySecretName !== undefined)
    entries.push({ key: 'AmplifyApiKeySecretName', value: data.amplifyApiKeySecretName });

  return entries;
}

// Settings API
export const settingsApi = {
  get: async (): Promise<AppSettings> => {
    const raw = await get<GlobalSettingDto[]>('/settings');
    return transformSettings(raw);
  },

  update: async (data: UpdateSettingsRequest): Promise<AppSettings> => {
    const entries = toSettingEntries(data);
    await Promise.all(
      entries.map((entry) => put(`/settings/${entry.key}`, { value: entry.value })),
    );
    // Re-fetch to return updated settings
    const raw = await get<GlobalSettingDto[]>('/settings');
    return transformSettings(raw);
  },
};

export const settingsKeys = {
  all: ['settings'] as const,
  detail: () => [...settingsKeys.all, 'detail'] as const,
};

export const teamsNotificationApi = {
  sendTest: (webhookUrl: string) =>
    post<TeamsTestNotificationResult>('/settings/notifications/test', { webhookUrl }),
};

// Module Configuration API (admin - uses /settings/modules for full access)
export const moduleConfigApi = {
  list: () => get<ModuleConfigurationAdmin[]>('/settings/modules'),

  get: (key: string) => get<ModuleConfigurationAdmin>(`/settings/modules/${key}`),

  create: (data: CreateModuleConfigRequest) =>
    post<ModuleConfigurationAdmin>('/settings/modules', data),

  update: (key: string, data: UpdateModuleConfigRequest) =>
    put<void>(`/settings/modules/${key}`, data),

  delete: (key: string) => del<void>(`/settings/modules/${key}`),

  testEndpoint: (key: string, endpoint: 'validation' | 'migration') =>
    post<{ success: boolean; message: string }>(`/settings/modules/${key}/test`, { endpoint }),
};

export const moduleConfigKeys = {
  all: ['moduleConfigs'] as const,
  lists: () => [...moduleConfigKeys.all, 'list'] as const,
  list: () => [...moduleConfigKeys.lists()] as const,
  details: () => [...moduleConfigKeys.all, 'detail'] as const,
  detail: (key: string) => [...moduleConfigKeys.details(), key] as const,
};

// User Management API
export const usersApi = {
  list: () => get<ApplicationUser[]>('/users'),

  get: (id: string) => get<ApplicationUser>(`/users/${id}`),

  // Authoritative source for the signed-in user's role — backed by the
  // ApplicationUsers DB row that admins manage at /admin/users. Used by
  // useAuth instead of client-side JWT decoding (which was brittle when
  // silent token acquisition failed or roles ended up only in one of the
  // ID/access tokens). Backend resolves identity from the validated JWT.
  getCurrentUser: () => get<ApplicationUser>('/users/me'),

  create: (data: CreateUserRequest) =>
    post<ApplicationUser>('/users', data),

  updateRole: (id: string, data: UpdateUserRoleRequest) =>
    put<ApplicationUser>(`/users/${id}/role`, data),

  remove: (id: string) => del<void>(`/users/${id}`),

  searchAzureAd: (query: string) =>
    get<AzureAdUser[]>('/users/search', { query }),
};

export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: () => [...usersKeys.lists()] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: string) => [...usersKeys.details(), id] as const,
  me: () => [...usersKeys.all, 'me'] as const,
  azureAdSearch: (query: string) => [...usersKeys.all, 'azureAd', query] as const,
};

// Audit Log API

interface RawAuditLogEntry extends Omit<AuditLogEntry, 'details'> {
  detailsJson: string | null;
}

function parseAuditLogDetails(entry: RawAuditLogEntry): AuditLogEntry {
  let details: Record<string, unknown> | null = null;
  if (entry.detailsJson) {
    try {
      details = JSON.parse(entry.detailsJson) as Record<string, unknown>;
    } catch {
      details = null;
    }
  }
  return { ...entry, details };
}

export const auditLogApi = {
  list: async (filters: AuditLogFilters = {}): Promise<PaginatedList<AuditLogEntry>> => {
    const result = await get<PaginatedList<RawAuditLogEntry>>('/audit-logs', filters);
    return {
      ...result,
      items: result.items.map(parseAuditLogDetails),
    };
  },

  get: async (id: string): Promise<AuditLogEntry> => {
    const result = await get<RawAuditLogEntry>(`/audit-logs/${id}`);
    return parseAuditLogDetails(result);
  },
};

export const auditLogKeys = {
  all: ['auditLogs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (filters: AuditLogFilters) => [...auditLogKeys.lists(), filters] as const,
  details: () => [...auditLogKeys.all, 'detail'] as const,
  detail: (id: string) => [...auditLogKeys.details(), id] as const,
};
